import { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import Modal from '../Modal';
import { getLocalDateString } from '../../utils/dateUtils';

const RECURRENCE_PATTERNS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' }
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}${getOrdinalSuffix(i + 1)}`
}));

function getOrdinalSuffix(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' }
];

const COLOR_OPTIONS = [
  '#C45D3A', // Terracotta
  '#5B6BD4', // Blue
  '#2D8A5F', // Green
  '#8B5CF6', // Purple
  '#E67E22', // Orange
  '#6B7280'  // Gray
];

function generateTimeOptions() {
  const options = [];
  for (let hour = 6; hour <= 21; hour++) {
    for (let min = 0; min < 60; min += 15) {
      const h = String(hour).padStart(2, '0');
      const m = String(min).padStart(2, '0');
      const time = `${h}:${m}`;
      const label = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      options.push({ value: time, label });
    }
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export default function MeetingSeriesModal({
  isOpen,
  onClose,
  onSave,
  editItem,
  templates = []
}) {
  const today = getLocalDateString(new Date());

  const [formData, setFormData] = useState({
    title: '',
    templateId: '',
    recurrencePattern: 'weekly',
    recurrenceDay: 1, // Monday
    recurrenceTime: '09:00',
    durationMinutes: 60,
    startDate: today,
    endDate: '',
    color: '#C45D3A',
    defaultAgendaItems: []
  });
  const [saving, setSaving] = useState(false);

  // Reset form when modal opens/closes or edit item changes
  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        setFormData({
          title: editItem.title || '',
          templateId: editItem.templateId || '',
          recurrencePattern: editItem.recurrencePattern || 'weekly',
          recurrenceDay: editItem.recurrenceDay ?? 1,
          recurrenceTime: editItem.recurrenceTime || '09:00',
          durationMinutes: editItem.durationMinutes || 60,
          startDate: editItem.startDate || today,
          endDate: editItem.endDate || '',
          color: editItem.color || '#C45D3A',
          defaultAgendaItems: editItem.defaultAgendaItems || []
        });
      } else {
        setFormData({
          title: '',
          templateId: '',
          recurrencePattern: 'weekly',
          recurrenceDay: 1,
          recurrenceTime: '09:00',
          durationMinutes: 60,
          startDate: today,
          endDate: '',
          color: '#C45D3A',
          defaultAgendaItems: []
        });
      }
    }
  }, [isOpen, editItem, today]);

  // Handle template selection
  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        templateId,
        title: prev.title || template.name,
        durationMinutes: template.defaultDurationMinutes || prev.durationMinutes,
        color: template.color || prev.color,
        defaultAgendaItems: (template.defaultAgendaItems || []).map(item => ({
          ...item,
          id: generateUUID()
        }))
      }));
    } else {
      setFormData(prev => ({ ...prev, templateId: '' }));
    }
  };

  // Handle agenda item changes
  const handleAddAgendaItem = () => {
    setFormData(prev => ({
      ...prev,
      defaultAgendaItems: [
        ...prev.defaultAgendaItems,
        {
          id: generateUUID(),
          title: '',
          description: '',
          durationMinutes: 10,
          order: prev.defaultAgendaItems.length
        }
      ]
    }));
  };

  const handleUpdateAgendaItem = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      defaultAgendaItems: prev.defaultAgendaItems.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleRemoveAgendaItem = (id) => {
    setFormData(prev => ({
      ...prev,
      defaultAgendaItems: prev.defaultAgendaItems.filter(item => item.id !== id)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    setSaving(true);
    try {
      await onSave({
        ...formData,
        endDate: formData.endDate || null
      });
    } finally {
      setSaving(false);
    }
  };

  const isMonthly = formData.recurrencePattern === 'monthly';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editItem ? 'Edit Recurring Series' : 'New Recurring Series'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5">
            Series Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="e.g., Weekly Leadership Sync"
            className="input-premium w-full"
            autoFocus
          />
        </div>

        {/* Template selector */}
        {templates.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Use Template (optional)
            </label>
            <select
              value={formData.templateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="input-premium w-full"
            >
              <option value="">No template</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Recurrence settings */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Repeats
            </label>
            <select
              value={formData.recurrencePattern}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                recurrencePattern: e.target.value,
                recurrenceDay: e.target.value === 'monthly' ? 1 : prev.recurrenceDay
              }))}
              className="input-premium w-full"
            >
              {RECURRENCE_PATTERNS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              {isMonthly ? 'Day of Month' : 'Day of Week'}
            </label>
            <select
              value={formData.recurrenceDay}
              onChange={(e) => setFormData(prev => ({ ...prev, recurrenceDay: Number(e.target.value) }))}
              className="input-premium w-full"
            >
              {(isMonthly ? DAYS_OF_MONTH : DAYS_OF_WEEK).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Time and Duration */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Time
            </label>
            <select
              value={formData.recurrenceTime}
              onChange={(e) => setFormData(prev => ({ ...prev, recurrenceTime: e.target.value }))}
              className="input-premium w-full"
            >
              {TIME_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Duration
            </label>
            <select
              value={formData.durationMinutes}
              onChange={(e) => setFormData(prev => ({ ...prev, durationMinutes: Number(e.target.value) }))}
              className="input-premium w-full"
            >
              {DURATION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Start and End dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Starts
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              className="input-premium w-full"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Ends (optional)
            </label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              className="input-premium w-full"
              min={formData.startDate}
            />
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5">
            Color
          </label>
          <div className="flex gap-2">
            {COLOR_OPTIONS.map(color => (
              <button
                key={color}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, color }))}
                className={`w-6 h-6 rounded-full transition-all ${
                  formData.color === color ? 'ring-2 ring-offset-2 ring-accent' : ''
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>

        {/* Default Agenda Items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-text-secondary">
              Default Agenda Items
            </label>
            <button
              type="button"
              onClick={handleAddAgendaItem}
              className="flex items-center gap-1 text-xs text-accent hover:text-accent-bright transition-colors"
            >
              <Plus size={12} />
              Add Item
            </button>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {formData.defaultAgendaItems.length === 0 ? (
              <p className="text-xs text-text-tertiary italic py-2">
                Add agenda items for each meeting in this series.
              </p>
            ) : (
              formData.defaultAgendaItems.map((item, index) => (
                <div
                  key={item.id}
                  className="flex items-start gap-2 p-2 bg-surface-hover rounded-lg"
                >
                  <GripVertical size={14} className="text-text-muted mt-2 flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => handleUpdateAgendaItem(item.id, 'title', e.target.value)}
                      placeholder="Agenda item title"
                      className="input-premium w-full text-sm"
                    />
                    <div className="flex gap-2">
                      <select
                        value={item.durationMinutes || 10}
                        onChange={(e) => handleUpdateAgendaItem(item.id, 'durationMinutes', Number(e.target.value))}
                        className="input-premium w-20 text-xs"
                      >
                        <option value={5}>5 min</option>
                        <option value={10}>10 min</option>
                        <option value={15}>15 min</option>
                        <option value={20}>20 min</option>
                        <option value={30}>30 min</option>
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveAgendaItem(item.id)}
                    className="p-1 text-text-muted hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-text-secondary bg-surface-hover hover:bg-surface-active rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!formData.title.trim() || saving}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-accent hover:bg-accent-bright rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : editItem ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
